import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTourAutoStart } from '@/hooks/useTourAutoStart';
import { adminBranchesTourId, adminBranchesSteps } from '@/tours/admin';
import {
  Building2,
  ShieldOff,
  ShieldCheck,
  MapPin,
  Phone,
  Clock,
  ChevronRight,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import {
  Card,
  Table,
  Pagination,
  SearchInput,
  Select,
  Modal,
  Button,
  LoadingSpinner,
  EmptyState,
  ErrorMessage,
} from '@/components/ui';
import { BranchStatusBadge } from '@/components/features/admin';
import { adminApi } from '@/services/api/adminApi';
import { queryKeys } from '@/services/api/queryClient';
import { cn } from '@/lib/utils';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const ITEMS_PER_PAGE = 20;

const DAY_LABELS: Record<string, string> = {
  monday: 'Пн',
  tuesday: 'Вт',
  wednesday: 'Ср',
  thursday: 'Чт',
  friday: 'Пт',
  saturday: 'Сб',
  sunday: 'Вс',
};

const DAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const statusOptions = [
  { value: '', label: 'Все статусы' },
  { value: 'APPROVED', label: 'Одобрен' },
  { value: 'PENDING', label: 'На модерации' },
  { value: 'REJECTED', label: 'Отклонён' },
  { value: 'BLOCKED', label: 'Заблокирован' },
];

const activeOptions = [
  { value: '', label: 'Все' },
  { value: 'true', label: 'Активные' },
  { value: 'false', label: 'Неактивные' },
];

interface AdminBranch {
  id: number;
  name: string;
  address: string | null;
  phone: string | null;
  latitude: number | null;
  longitude: number | null;
  working_hours: Record<string, { open: string; close: string } | null> | null;
  status: string;
  is_active: boolean;
  created_at: string;
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatWorkingHours(hours: Record<string, { open: string; close: string } | null> | null): React.ReactNode {
  if (!hours) return <span className="text-gray-400">Не указано</span>;

  return (
    <div className="grid grid-cols-1 gap-1.5">
      {DAY_ORDER.map((day) => {
        const schedule = hours[day];
        return (
          <div key={day} className="flex items-center gap-2 text-sm">
            <span className="w-6 text-gray-500 font-medium">{DAY_LABELS[day]}</span>
            {schedule ? (
              <span className="text-gray-700">{schedule.open} — {schedule.close}</span>
            ) : (
              <span className="text-gray-400">Выходной</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

const AdminBranchesPage: React.FC = () => {
  useTourAutoStart({ tourId: adminBranchesTourId, steps: adminBranchesSteps });
  const queryClientHook = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [selectedBranch, setSelectedBranch] = useState<AdminBranch | null>(null);

  const filtersKey = JSON.stringify({ search, status: statusFilter, is_active: activeFilter, page });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.adminBranches(filtersKey),
    queryFn: () =>
      adminApi.getBranches({
        search: search || undefined,
        status: statusFilter || undefined,
        is_active: activeFilter === '' ? undefined : activeFilter === 'true',
        page,
        limit: ITEMS_PER_PAGE,
      }),
  });

  const blockMutation = useMutation({
    mutationFn: (id: number) => adminApi.blockBranch(id),
    onSuccess: () => {
      toast.success('ОЦ заблокирован');
      queryClientHook.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Не удалось заблокировать')),
  });

  const unblockMutation = useMutation({
    mutationFn: (id: number) => adminApi.unblockBranch(id),
    onSuccess: () => {
      toast.success('ОЦ разблокирован');
      queryClientHook.invalidateQueries({ queryKey: ['admin'] });
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Не удалось разблокировать')),
  });

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setPage(1);
  }, []);

  const handleActiveChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setActiveFilter(e.target.value);
    setPage(1);
  }, []);

  const handleBlockToggle = useCallback((branch: AdminBranch, e: React.MouseEvent) => {
    e.stopPropagation();
    if (branch.is_active) {
      blockMutation.mutate(branch.id);
    } else {
      unblockMutation.mutate(branch.id);
    }
  }, [blockMutation, unblockMutation]);

  const branches = (data?.items ?? []) as AdminBranch[];
  const totalCount = data?.total ?? 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Операционные центры</h1>
        <p className="text-sm text-gray-500 mt-1">
          Управление операционными центрами
          {totalCount > 0 && <span className="text-gray-400"> · {totalCount} всего</span>}
        </p>
      </div>

      <Card>
        <Card.Header>
          <div className="flex flex-col sm:flex-row gap-3">
            <div data-tour="admin:branches-search" className="flex-1">
              <SearchInput
                value={search}
                onChange={handleSearchChange}
                placeholder="Поиск по названию, адресу..."
                className="w-full"
              />
            </div>
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={handleStatusChange}
              className="sm:w-44"
            />
            <Select
              options={activeOptions}
              value={activeFilter}
              onChange={handleActiveChange}
              className="sm:w-36"
            />
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          <div data-tour="admin:branches-list">
          {isLoading ? (
            <LoadingSpinner size="lg" className="py-16" />
          ) : error ? (
            <ErrorMessage message="Не удалось загрузить список ОЦ" onRetry={() => refetch()} className="py-12" />
          ) : branches.length === 0 ? (
            <EmptyState
              icon={<Building2 className="w-8 h-8" />}
              title="ОЦ не найдены"
              description={search || statusFilter || activeFilter ? 'Попробуйте изменить параметры поиска' : 'Пока нет зарегистрированных ОЦ'}
            />
          ) : (
            <>
              {/* Mobile */}
              <div className="md:hidden divide-y divide-gray-100">
                {branches.map((branch) => (
                  <button
                    key={branch.id}
                    onClick={() => setSelectedBranch(branch)}
                    className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50/50 transition-colors text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">{branch.name}</p>
                        <BranchStatusBadge status={branch.status} />
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span>{formatDate(branch.created_at)}</span>
                        {branch.address && <span className="truncate">{branch.address}</span>}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                  </button>
                ))}
              </div>

              {/* Desktop */}
              <div className="hidden md:block">
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.Head>Название</Table.Head>
                      <Table.Head>Адрес</Table.Head>
                      <Table.Head>Телефон</Table.Head>
                      <Table.Head>Статус</Table.Head>
                      <Table.Head>Активен</Table.Head>
                      <Table.Head>Дата создания</Table.Head>
                      <Table.Head className="text-right">Действия</Table.Head>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {branches.map((branch) => (
                      <Table.Row
                        key={branch.id}
                        onClick={() => setSelectedBranch(branch)}
                      >
                        <Table.Cell className="font-medium text-gray-900 max-w-[200px] truncate">
                          {branch.name}
                        </Table.Cell>
                        <Table.Cell className="max-w-[200px] truncate">
                          {branch.address || '—'}
                        </Table.Cell>
                        <Table.Cell>
                          {branch.phone || '—'}
                        </Table.Cell>
                        <Table.Cell>
                          <BranchStatusBadge status={branch.status} />
                        </Table.Cell>
                        <Table.Cell>
                          {branch.is_active ? (
                            <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Да
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-gray-400 text-xs font-medium">
                              <XCircle className="w-3.5 h-3.5" />
                              Нет
                            </span>
                          )}
                        </Table.Cell>
                        <Table.Cell>
                          {formatDate(branch.created_at)}
                        </Table.Cell>
                        <Table.Cell className="text-right">
                          <Button
                            variant={branch.is_active ? 'ghost' : 'outline'}
                            size="sm"
                            onClick={(e) => handleBlockToggle(branch, e)}
                            loading={
                              (blockMutation.isPending && blockMutation.variables === branch.id) ||
                              (unblockMutation.isPending && unblockMutation.variables === branch.id)
                            }
                            className={cn(
                              branch.is_active
                                ? 'text-red-600 hover:bg-red-50 hover:text-red-700'
                                : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50',
                            )}
                          >
                            {branch.is_active ? (
                              <>
                                <ShieldOff className="w-3.5 h-3.5" />
                                Блок.
                              </>
                            ) : (
                              <>
                                <ShieldCheck className="w-3.5 h-3.5" />
                                Разблок.
                              </>
                            )}
                          </Button>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </div>
            </>
          )}
          </div>
        </Card.Body>

        {totalPages > 1 && (
          <Card.Footer>
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </Card.Footer>
        )}
      </Card>

      {/* Branch Detail Modal */}
      <Modal
        isOpen={selectedBranch !== null}
        onClose={() => setSelectedBranch(null)}
        title={selectedBranch?.name ?? 'Детали ОЦ'}
        description="Подробная информация об операционном центре"
        size="lg"
      >
        {selectedBranch && (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <BranchStatusBadge status={selectedBranch.status} size="md" />
              {selectedBranch.is_active ? (
                <span className="inline-flex items-center gap-1 text-emerald-600 text-sm font-medium">
                  <CheckCircle2 className="w-4 h-4" />
                  Активен
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-gray-400 text-sm font-medium">
                  <XCircle className="w-4 h-4" />
                  Неактивен
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <DetailField
                icon={Building2}
                label="Название"
                value={selectedBranch.name}
              />
              <DetailField
                icon={MapPin}
                label="Адрес"
                value={selectedBranch.address}
              />
              <DetailField
                icon={Phone}
                label="Телефон"
                value={selectedBranch.phone}
              />
              <DetailField
                icon={Clock}
                label="Дата создания"
                value={formatDate(selectedBranch.created_at)}
              />
            </div>

            {(selectedBranch.latitude !== null && selectedBranch.longitude !== null) && (
              <div className="p-3 bg-gray-50 rounded-xl">
                <p className="text-xs font-medium text-gray-500 mb-1">Координаты</p>
                <p className="text-sm text-gray-700 font-mono">
                  {selectedBranch.latitude?.toFixed(6)}, {selectedBranch.longitude?.toFixed(6)}
                </p>
              </div>
            )}

            {selectedBranch.working_hours && (
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-gray-400" />
                  Рабочие часы
                </p>
                <div className="p-3 bg-gray-50 rounded-xl">
                  {formatWorkingHours(selectedBranch.working_hours)}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-2 border-t border-gray-100">
              <Button
                variant={selectedBranch.is_active ? 'danger' : 'primary'}
                size="sm"
                onClick={(e) => {
                  handleBlockToggle(selectedBranch, e);
                  setSelectedBranch(null);
                }}
                loading={blockMutation.isPending || unblockMutation.isPending}
              >
                {selectedBranch.is_active ? (
                  <>
                    <ShieldOff className="w-4 h-4" />
                    Заблокировать
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4" />
                    Разблокировать
                  </>
                )}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedBranch(null)}>
                Закрыть
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

function DetailField({ icon: Icon, label, value }: { icon: React.FC<{ className?: string }>; label: string; value: string | null }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-gray-500" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-medium text-gray-900">{value || '—'}</p>
      </div>
    </div>
  );
}

export default AdminBranchesPage;
